import { useEventCallback } from "@src/appV2/lib/utils/useEventCallback";
import { useFlowStack } from "@src/appV2/lib/utils/useFlowStack";
import { useCallback } from "react";

export type ECClockOutFlowStep = "emergencyCredits" | "clipboardScore" | "restricted";

interface CreditStats {
  totalAvailableCredits: number;
  creditCap: number;
}

interface UseECClockOutFlowConfig {
  areBothCreditsMaxed: boolean;
  gainedCancellationCredit: boolean;
  gainedLatenessCredit: boolean;
  usedLatenessCredit: boolean;
  cancellationCreditStats: CreditStats;
  latenessCreditStats: CreditStats;
  newlyRestricted: boolean;
  hasReEnrollmentScore: boolean;
  onExit: () => void;
}

interface UseECClockOutFlowResult {
  /**
   * The effective current step. When `areBothCreditsMaxed` is true and the internal
   * flow stack is on "emergencyCredits", this returns "clipboardScore" instead —
   * skipping the EC page since there's nothing new to show.
   */
  currentStep: ECClockOutFlowStep;
  canGoBack: boolean;
  handleEmergencyCreditsContinue: () => void;
  handleClipboardScoreContinue: () => void;
  handleRestrictedContinue: () => void;
  reset: () => void;
}

export function useECClockOutFlow(config: UseECClockOutFlowConfig): UseECClockOutFlowResult {
  const flow = useFlowStack<ECClockOutFlowStep>("emergencyCredits");

  const handleEmergencyCreditsContinue = useEventCallback(() => {
    flow.push("clipboardScore");
  });

  const handleClipboardScoreContinue = useEventCallback(() => {
    if (config.newlyRestricted && config.hasReEnrollmentScore) {
      flow.push("restricted");
    } else {
      config.onExit();
    }
  });

  const handleRestrictedContinue = useEventCallback(() => {
    config.onExit();
  });

  const reset = useCallback(() => {
    flow.reset();
  }, [flow]);

  // When a lateness credit was auto-consumed during clock-out, the profile reflects the
  // decremented count. We restore the pre-spend count to determine skip eligibility.
  const wereCreditsMaxedBeforeSpend =
    config.usedLatenessCredit &&
    config.cancellationCreditStats.totalAvailableCredits >=
      config.cancellationCreditStats.creditCap &&
    config.latenessCreditStats.totalAvailableCredits + 1 >= config.latenessCreditStats.creditCap;

  // True only when both credits were already maxed before this shift (neither was just earned).
  // If either credit was just earned, show YourEmergencyCreditsPage first so the worker sees the celebration UI.
  const shouldSkipEmergencyCredits =
    !config.gainedCancellationCredit &&
    !config.gainedLatenessCredit &&
    (config.areBothCreditsMaxed || wereCreditsMaxedBeforeSpend);

  const currentStep: ECClockOutFlowStep =
    flow.currentStep === "emergencyCredits" && shouldSkipEmergencyCredits
      ? "clipboardScore"
      : flow.currentStep;

  return {
    currentStep,
    canGoBack: flow.canGoBack,
    handleEmergencyCreditsContinue,
    handleClipboardScoreContinue,
    handleRestrictedContinue,
    reset,
  };
}
